/**
 * `Sheet` (W1-12 ui-kit-i18n; contract SheetProps, DESIGN.md §4.14): a NON-modal side sheet for generators
 * (Distribute, Fill, Motion path, ...). The timeline and preview stay interactive behind it: no scrim, no focus
 * trap, clicks outside do not close it, and Escape closes it only while focus is inside the sheet (elsewhere
 * Escape keeps its editor meaning). It is a labelled dialog region (role="dialog", not aria-modal) with a close
 * button, a scrolling body and a sticky footer for Apply / Cancel.
 *
 * Focus starts on the first enabled control of the body, else the footer's primary button, else the sheet itself,
 * never on the close button (internal/layers.ts), so Escape right after opening closes it. An Escape that reverts
 * a field's typed text (data-pending-edit) stays with the field.
 */

import * as RD from '@radix-ui/react-dialog';
import { useRef } from 'react';
import { IconButton } from './IconButton';
import { cx } from './internal/cx';
import { escapeHasLocalMeaning, focusInitial, useOpener } from './internal/layers';
import s from './Sheet.module.css';
import type { SheetProps } from './types';
import { m } from './ui.messages';

export function Sheet(props: SheetProps) {
  const side = props.side ?? 'right';
  const contentRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const opener = useOpener(props.open);
  return (
    <RD.Root
      open={props.open}
      modal={false}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <RD.Portal>
        <RD.Content
          ref={contentRef}
          className={cx(s.sheet, side === 'left' ? s.left : s.right)}
          data-side={side}
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            focusInitial({ content: contentRef.current, body: bodyRef.current, footer: footerRef.current });
          }}
          onCloseAutoFocus={(e) => {
            // Give focus back to the opener only when focus was in the sheet (the user may be in the timeline).
            e.preventDefault();
            const target = opener;
            const active = document.activeElement;
            const lost = active === null || active === document.body || contentRef.current?.contains(active) === true;
            if (lost && target?.isConnected === true) target.focus({ preventScroll: true });
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            const content = contentRef.current;
            if (content === null || !content.contains(document.activeElement) || escapeHasLocalMeaning(e)) {
              e.preventDefault();
            }
          }}
        >
          <header className={s.header}>
            <RD.Title className={s.title}>{props.title}</RD.Title>
            <IconButton
              icon="X"
              label={{ key: m.close }}
              onClick={() => {
                props.onClose();
              }}
            />
          </header>
          <div ref={bodyRef} className={s.body}>
            {props.children}
          </div>
          {props.footer !== undefined && props.footer !== null && (
            <footer ref={footerRef} className={s.footer}>
              {props.footer}
            </footer>
          )}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
